/**
 * #1532 — Retained view state for the request list (home): filters, sort, search text,
 * page size and current page.
 *
 * `App.tsx` renders exactly one screen per route, so `ViewRequests` is unmounted the moment
 * the router switches to `/request-detail`. Every `useState` in it is gone by the time the
 * user presses Back, which is why a filtered page 3 at 100 rows used to come back as an
 * unfiltered page 1 at 20. This module keeps that state outside the component.
 *
 * Two layers, and both are read **synchronously** so the very first render is already correct.
 * A post-mount async restore would run the paginated query twice and flash page 1 of the
 * unfiltered list before settling:
 *
 *   - in-memory map — the whole story on native, where in-app navigation never drops the module.
 *   - `localStorage`, web only — survives a browser refresh and the forced full page reload
 *     `App.tsx` performs when navigating request -> request. Written straight to
 *     `window.localStorage` rather than through `services/storage` because that wrapper is
 *     async-only; `requestDraftSnapshot.writeSnapshotSync` goes direct for the same reason.
 *     AsyncStorage has no synchronous read, so native deliberately gets no durable layer —
 *     an app restart there starts on the default view.
 *
 * Keyed per user (same convention as `requestDraftSnapshot`) so a shared browser, and an
 * impersonation session, never inherit someone else's filters.
 *
 * Everything coming back out of storage is untrusted and validated field by field against
 * the caller's defaults: a hand-edited or stale `localStorage` entry must degrade to the
 * default view, never to a broken one. The defaults are passed in rather than owned here
 * because `ViewRequests` already owns them (`DEFAULT_SORT`, `DEFAULT_PAGE_SIZE`).
 */

import { Platform } from 'react-native';

const STORAGE_PREFIX = 'request-list-view:';
const STATE_VERSION = 1 as const;

/** The server binds `searchText` as NVARCHAR(255); no point retaining more than it can use. */
const MAX_TEXT_LENGTH = 255;

/** An absurd page number is a tampered or corrupt value, not a user intent. */
const MAX_PAGE = 10_000;

/**
 * Page sizes offered by the "Rows per page" dropdown, and the restore whitelist — a stored
 * size outside this list would highlight nothing in that dropdown. `ViewRequests` renders
 * the options from this same constant so the two cannot drift.
 */
export const REQUEST_LIST_PAGE_SIZES: readonly number[] = [10, 20, 50, 100];

/**
 * Sortable fields, mirroring the sort dropdown in `ViewRequests` (and the `orderBy` mapping
 * in its `paginationParams`). A field missing here still sorts normally within the session;
 * it just falls back to the default order when the view is restored — so a new sort field
 * belongs in both places.
 */
export const REQUEST_LIST_SORT_FIELDS: readonly string[] = [
  'dateCreated',
  'sysModifiedOn',
  'customerName',
  'id',
  'requestorName',
  'region',
  'stage',
];

export interface RequestListFilterOptions {
  region: string;
  modifiedOn: string;
  modifiedBy: string;
  stage: string;
  readStatus: string;
}

export interface RequestListSortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

export interface RequestListViewState {
  searchText: string;
  pageSize: number;
  currentPage: number;
  filterOptions: RequestListFilterOptions;
  sortOptions: RequestListSortOptions;
}

const memoryStore = new Map<string, RequestListViewState>();

/** Values go straight into `setState`, so never hand back a caller's shared default object. */
function cloneViewState(state: RequestListViewState): RequestListViewState {
  return {
    searchText: state.searchText,
    pageSize: state.pageSize,
    currentPage: state.currentPage,
    filterOptions: { ...state.filterOptions },
    sortOptions: { ...state.sortOptions },
  };
}

function sanitizeUserKey(userEmail: string | null | undefined): string {
  const trimmed = userEmail?.trim().toLowerCase();
  return trimmed && trimmed.length > 0 ? trimmed : 'anonymous';
}

function storageKey(userEmail: string | null | undefined): string {
  return `${STORAGE_PREFIX}${sanitizeUserKey(userEmail)}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `null` on native, and on web when storage is unavailable (private mode, blocked site data). */
function getLocalStorage(): Storage | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

function sanitizeText(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value.slice(0, MAX_TEXT_LENGTH) : fallback;
}

function sanitizePageSize(value: unknown, fallback: number): number {
  return typeof value === 'number' && REQUEST_LIST_PAGE_SIZES.includes(value)
    ? value
    : fallback;
}

function sanitizeCurrentPage(value: unknown, fallback: number): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= MAX_PAGE
    ? value
    : fallback;
}

function sanitizeSortOptions(
  value: unknown,
  fallback: RequestListSortOptions,
): RequestListSortOptions {
  if (!isPlainObject(value)) {
    return { ...fallback };
  }
  return {
    field:
      typeof value.field === 'string' &&
      REQUEST_LIST_SORT_FIELDS.includes(value.field)
        ? value.field
        : fallback.field,
    direction:
      value.direction === 'asc' || value.direction === 'desc'
        ? value.direction
        : fallback.direction,
  };
}

function sanitizeFilterOptions(
  value: unknown,
  fallback: RequestListFilterOptions,
): RequestListFilterOptions {
  if (!isPlainObject(value)) {
    return { ...fallback };
  }
  return {
    region: sanitizeText(value.region, fallback.region),
    modifiedOn: sanitizeText(value.modifiedOn, fallback.modifiedOn),
    modifiedBy: sanitizeText(value.modifiedBy, fallback.modifiedBy),
    stage: sanitizeText(value.stage, fallback.stage),
    readStatus: sanitizeText(value.readStatus, fallback.readStatus),
  };
}

function parseStoredState(
  raw: string | null,
  defaults: RequestListViewState,
): RequestListViewState | null {
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isPlainObject(parsed) || parsed.version !== STATE_VERSION) {
    return null;
  }
  const state = parsed.state;
  if (!isPlainObject(state)) {
    return null;
  }
  return {
    searchText: sanitizeText(state.searchText, defaults.searchText),
    pageSize: sanitizePageSize(state.pageSize, defaults.pageSize),
    currentPage: sanitizeCurrentPage(state.currentPage, defaults.currentPage),
    filterOptions: sanitizeFilterOptions(
      state.filterOptions,
      defaults.filterOptions,
    ),
    sortOptions: sanitizeSortOptions(state.sortOptions, defaults.sortOptions),
  };
}

/**
 * The retained view for this user, or a copy of `defaults` when nothing is retained.
 *
 * Synchronous by design — `ViewRequests` calls this from a `useState` initializer so the
 * first render, and therefore the first paginated query, already carries the retained
 * filters, sort, page size and page.
 */
export function readRequestListViewState(
  userEmail: string | null | undefined,
  defaults: RequestListViewState,
): RequestListViewState {
  const key = sanitizeUserKey(userEmail);
  const inMemory = memoryStore.get(key);
  if (inMemory) {
    return cloneViewState(inMemory);
  }

  const storage = getLocalStorage();
  if (storage) {
    try {
      const restored = parseStoredState(
        storage.getItem(storageKey(userEmail)),
        defaults,
      );
      if (restored) {
        memoryStore.set(key, restored);
        return cloneViewState(restored);
      }
    } catch {
      // Unreadable storage is not worth failing the screen over.
    }
  }

  return cloneViewState(defaults);
}

/** Mirrors the live view state into the retention layers. Never throws. */
export function writeRequestListViewState(
  userEmail: string | null | undefined,
  state: RequestListViewState,
): void {
  memoryStore.set(sanitizeUserKey(userEmail), cloneViewState(state));

  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(
      storageKey(userEmail),
      JSON.stringify({ version: STATE_VERSION, state }),
    );
  } catch {
    // Quota exceeded or storage blocked — in-session retention still works.
  }
}

/** Called on logout, so a shared device does not keep the previous user's view. */
export function clearRequestListViewState(
  userEmail: string | null | undefined,
): void {
  memoryStore.delete(sanitizeUserKey(userEmail));

  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(storageKey(userEmail));
  } catch {
    // Nothing actionable.
  }
}

/** Test-only */
export function resetRequestListViewStateForTests(): void {
  memoryStore.clear();
}
